import {
  AccountData,
  Conversation,
  ConversationPreset,
  MemberIndicator,
  NotificationType,
} from "@markit/common.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Query,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "../../firebase";
import { AppState, AppThunk } from "../store";
import { API } from "../../API";
import {
  Batch,
  getUserConversationRef,
  getUserConversationsRef,
  getUserData,
  getUserFollowerData,
} from "../../utils/FirebaseUtils";
import { batch } from "react-redux";
import { accountActions } from "./accountSlice";
import { filterUndefinedValues } from "@markit/common.utils";
import { fetchConversationPresets } from "../../utils/conversationUtils";
import { showNotificationBanner } from "../../utils/notificationUtils";

export type ConversationState = {
  userConversations: Conversation[];
  conversationPresets: ConversationPreset[];
  loadedConversationPresets: boolean;
};

export const initialConversationState: ConversationState = {
  userConversations: [],
  conversationPresets: [],
  loadedConversationPresets: false,
};

// TODO (PETER): Currently, userConversations redux state is not used for anything, since the logic for fetching conversations was moved to a useLoad hook.
// In the future, modify the logic to fetch from redux instead of firebase when fetching conversations in the useLoad hook logic
export const conversationSlice = createSlice({
  name: "conversation",
  initialState: initialConversationState,
  reducers: {
    addMultipleConversations: (
      state,
      action: PayloadAction<Conversation[]>
    ) => {
      state.userConversations = Array.from(
        [...state.userConversations, ...action.payload]
          .reduce((map, item) => {
            map.set(item.uid, item);
            return map;
          }, new Map())
          .values()
      );
    },
    removeConversation: (state, action: PayloadAction<string>) => {
      state.userConversations = state.userConversations.filter(
        (c) => c.uid !== action.payload
      );
    },
    removeAllConversations: (state) => {
      state.userConversations = [];
    },
    updateReadConversationIndicator: (
      state,
      action: PayloadAction<{
        participantUid: string;
        uid: string;
        markRead: boolean;
      }>
    ) => {
      // for all conversations
      const index = state.userConversations.findIndex(
        (c) => c.uid === action.payload.participantUid
      );
      if (index !== -1) {
        const newChatIndicator: MemberIndicator[] = state.userConversations[
          index
        ].chatIndicator.map((m: { uid: string; indicator: boolean }) => {
          if (m.uid === action.payload.uid) {
            return { uid: m.uid, indicator: action.payload.markRead };
          } else {
            return m;
          }
        });
        state.userConversations[index].chatIndicator = newChatIndicator;
      }
    },
    updateArchivedStatus: (
      state,
      action: PayloadAction<{ participantUid: string; archived: boolean }>
    ) => {
      const index = state.userConversations.findIndex(
        (c) => c.uid === action.payload.participantUid
      );
      if (index !== -1) {
        state.userConversations[index].archived = action.payload.archived;
      }
    },
    updateAllArchivedStatus: (state, action: PayloadAction<boolean>) => {
      state.userConversations = state.userConversations.map((c) => ({
        ...c,
        archived: action.payload,
      }));
    },
    setConversationPresets: (
      state,
      action: PayloadAction<ConversationPreset[]>
    ) => {
      state.conversationPresets = action.payload;
    },
    addConversationPreset: (
      state,
      action: PayloadAction<ConversationPreset>
    ) => {
      state.conversationPresets.unshift(action.payload);
    },
    updateConversationPreset: (
      state,
      action: PayloadAction<ConversationPreset>
    ) => {
      state.conversationPresets = state.conversationPresets.map((preset) =>
        preset.id === action.payload.id ? action.payload : preset
      );
    },
    removeConversationPreset: (state, action: PayloadAction<string>) => {
      state.conversationPresets = state.conversationPresets.filter(
        (p) => p.id !== action.payload
      );
    },
    setLoadedConversationPresets: (state, action: PayloadAction<boolean>) => {
      state.loadedConversationPresets = action.payload;
    },
  },
});

export const conversationActions = conversationSlice.actions;
export const conversationReducer = conversationSlice.reducer;

// Fetch the three most recent conversations (for display on home page)
export const fetchInitialConversations =
  (userId: string): AppThunk =>
  async (dispatch) => {
    const userConversationsRef = getUserConversationsRef(userId);
    const baseQuery = [
      where("archived", "==", false),
      where("recentTimestamp", ">", ""),
      orderBy("recentTimestamp", "desc"),
      limit(3),
    ];

    const userConversationsQuery = query(userConversationsRef, ...baseQuery);
    const conversationsSnap = await getDocs(userConversationsQuery);
    const conversationData = await Promise.all(
      conversationsSnap.docs
        .map((doc) => doc.data())
        .map(async (conversation) => {
          const [userData, followerData] = await Promise.all([
            getUserData(conversation.uid),
            getUserFollowerData(userId, conversation.uid),
          ]);
          return { conversation, userData, followerData };
        })
    );
    const definedUsers = filterUndefinedValues(
      conversationData.map((c) => c.userData)
    );
    const definedFollowers = filterUndefinedValues(
      conversationData.map((c) => c.followerData)
    );
    batch(() => {
      dispatch(
        conversationActions.addMultipleConversations(
          conversationData.map((c) => c.conversation)
        )
      );
      dispatch(accountActions.addMultipleFollowingAccountData(definedUsers));
      dispatch(accountActions.addMultipleFollowerData(definedFollowers));
    });
  };

// Mark the message that was previously unread as read if markRead is true
// Mark the message that was previously read as unread if markRead is false
export const updateChatConversationIndicator =
  (participantUid: string, uid: string, markRead: boolean): AppThunk =>
  async (dispatch) => {
    const batch = new Batch("Error updating chat conversation indicator");
    const conversationRef = getUserConversationRef(uid, participantUid);
    const conversationData = (await getDoc(conversationRef)).data();
    if (conversationData) {
      const newChatIndicator: MemberIndicator[] =
        conversationData.chatIndicator.map(
          (m: { uid: string; indicator: boolean }) => {
            if (m.uid === uid) {
              return { uid: m.uid, indicator: markRead };
            } else {
              return m;
            }
          }
        );
      batch.update(conversationRef, { chatIndicator: newChatIndicator });
      // Update read indicator in Firebase
      dispatch(
        conversationActions.updateReadConversationIndicator({
          participantUid,
          uid,
          markRead,
        })
      );
    }

    try {
      await batch.commit();
    } catch (e: any) {
      alert("Error updating chat indicator: " + e.message);
    }
  };

export const updateConversationArchivedStatus =
  (conversation: Conversation): AppThunk =>
  async (dispatch, getState) => {
    const { accountData } = getState().account;
    const newArchivedStatus = !(conversation.archived ?? false);
    const batch = new Batch("Error updating conversation archived status");
    batch.update(getUserConversationRef(accountData.uid, conversation.uid), {
      archived: newArchivedStatus,
    });

    try {
      await batch.commit();
      dispatch(
        conversationActions.updateArchivedStatus({
          participantUid: conversation.uid,
          archived: newArchivedStatus,
        })
      );
    } catch (e: any) {
      alert("Error updating conversation archived status: " + e.message);
    }
  };

/**
 * Gets the userData of the participants from all of a user's conversations.
 */
export const getConversationParticipantsData = async (
  userConversations: Conversation[]
): Promise<AccountData[]> => {
  const participantsData = await Promise.all(
    userConversations.map(async (conversation) => {
      const userData = await getUserData(conversation.uid);
      if (userData) {
        return userData;
      }
    })
  );
  const definedParticipants: AccountData[] = [];
  for (let i = 0; i < participantsData.length; i++) {
    if (participantsData[i] as AccountData) {
      definedParticipants.push(participantsData[i] as AccountData);
    }
  }
  return definedParticipants;
};

// conversationId is the participant's uid
export const deleteSingleConversation =
  (conversationId: string, userId: string): AppThunk =>
  async (dispatch) => {
    await API.creatorText
      .deleteTwilioConversation({
        conversationIdToDelete: conversationId,
        userId,
      })
      .then(() => {
        dispatch(conversationActions.removeConversation(conversationId));
        showNotificationBanner(
          dispatch,
          "Deleted Conversation",
          NotificationType.NEGATIVE
        );
      });
  };

export const getUnreadConversationsQuery = async (
  userId: string
): Promise<Query<Conversation>> => {
  const userConversationsRef = getUserConversationsRef(userId);
  const query_ = query(
    userConversationsRef,
    where("chatIndicator", "array-contains", { indicator: true, uid: userId })
  );

  return query_;
};

export const isUnreadConversation = (
  chatIndicators: MemberIndicator[],
  userId: string
) => {
  if (chatIndicators !== undefined) {
    return chatIndicators.some(
      (member) => member.indicator && member.uid === userId
    );
  } else {
    return false;
  }
};

export const fetchNumTotalConversations = async (
  userId: string
): Promise<number> => {
  const userConversationsRef = getUserConversationsRef(userId);
  const query_ = query(
    userConversationsRef,
    where("recentTimestamp", "!=", "")
  );
  const snapshot = await getCountFromServer(query_);

  return snapshot.data().count;
};

export const fetchInitialConversationPresets =
  (): AppThunk => async (dispatch, getState) => {
    const { accountData } = getState().account;
    const presets = await fetchConversationPresets(accountData);
    dispatch(conversationActions.setConversationPresets(presets));
    dispatch(conversationActions.setLoadedConversationPresets(true));
  };

export const archiveAllConversations = (): AppThunk => async (dispatch) => {
  await API.conversation.archiveAllTwilioConversations({});
  // Update the archived status of all conversations in redux
  dispatch(conversationActions.updateAllArchivedStatus(true));
  showNotificationBanner(dispatch, "Archived All", NotificationType.NEGATIVE);
};

export const deleteAllVisibleConversations =
  (): AppThunk => async (dispatch) => {
    await API.conversation.deleteAllVisibleTwilioConversations({});
    // Remove all conversations from redux
    dispatch(conversationActions.removeAllConversations());
    showNotificationBanner(dispatch, "Deleted All", NotificationType.NEGATIVE);
  };

export const getConversationState = (state: AppState) => state.conversations;
